import notfound from '../assets/images/notfound.png';
import React, { useState, useRef, useEffect } from 'react';
import { Link, useParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import Ariane from './Ariane';
import BottomNavigation from './BottomNavigation'
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, EffectCreative } from 'swiper';
import { useCart } from "react-use-cart";
import { MiniCart, QuantitySelector, formatCurrency, isDiscounted, getDiscountPrice, getClientGeneralDiscount, getFinalDiscountPercentage, getFinalDiscountPrice, getAppliedDiscountType, getFreeProductMinQty, getFreeProductBaseData } from './Cart';
import DataTable, { createTheme } from 'react-data-table-component';
import bs5Styles from '../assets/themes/bs5';
import Select from "react-select";
import { useCustomFields } from '../forms/CustomFields';
import { useIndexedDB } from 'react-indexed-db';
import { strings } from '../localization';
import { set, format } from 'date-fns';
import { SwitchTransition, CSSTransition } from "react-transition-group";
import "./styles.css";
import createDOMPurify from 'dompurify'
import { useApi } from './Api';

const isProductOnline = (product) => {
    const onlineValue = product?.online;

    return onlineValue === undefined ||
        onlineValue === null ||
        onlineValue === true ||
        onlineValue === 1 ||
        onlineValue === "1";
}

const productMatchesCategory = (product, categoryRef) => {
    if (!categoryRef) {
        return true;
    }

    if (!product?.categories) {
        return false;
    }

    let category = product.categories;
    while (category) {
        if (String(category.ref) === String(categoryRef)) {
            return true;
        }
        category = category.parent ?? null;
    }

    return false;
}

const normalizeBarcodeValue = (value) => {
    if (value === null || value === undefined) {
        return "";
    }

    const rawValue = String(value).trim();
    if (!rawValue) {
        return "";
    }

    const normalizedScientificValue = rawValue.replace(",", ".");
    if (/^[0-9]+(?:\.[0-9]+)?e\+?[0-9]+$/i.test(normalizedScientificValue)) {
        const numericValue = Number(normalizedScientificValue);
        if (Number.isFinite(numericValue)) {
            return Math.trunc(numericValue).toString();
        }
    }

    return rawValue.replace(/\D/g, "");
}

const getBarcodeUrl = (product) => {
    const barcodeValue = normalizeBarcodeValue(product?.bar_code);

    return barcodeValue ? "https://sales.faye-sas.fr/barcode.php?pid=" + encodeURIComponent(barcodeValue) : "";
}

const getBarcodeLabel = (product) => normalizeBarcodeValue(product?.bar_code);

const getFavoriteProductRefs = (favoriteProducts) => {
    if (!favoriteProducts) {
        return [];
    }

    if (Array.isArray(favoriteProducts)) {
        return favoriteProducts.map(ref => String(ref).trim()).filter(Boolean);
    }

    return String(favoriteProducts).split(",").map(ref => ref.trim()).filter(Boolean);
}

const toggleFavoriteProductRef = (favoriteProducts, productRef, removeOnly = false) => {
    const refs = getFavoriteProductRefs(favoriteProducts);
    const normalizedRef = String(productRef).trim();
    const index = refs.findIndex(ref => ref === normalizedRef);

    if (index !== -1) {
        refs.splice(index, 1);
    } else if (!removeOnly && normalizedRef) {
        refs.push(normalizedRef);
    }

    return refs.join(",");
}

const normalizeSearchTerm = (value) => String(value ?? "").trim();

const getSessionJson = (key, fallback = null) => {
    try {
        const value = sessionStorage.getItem(key);
        return value ? JSON.parse(value) : fallback;
    } catch (e) {
        return fallback;
    }
}

const getStoredCurrentClient = () => getSessionJson("orders.current_client", null);
const getStoredDemoClientAccess = () => getSessionJson("orders.demo_client_access", false) === true;

const normalizePositiveInteger = (value, fallback = 1) => {
    const parsedValue = parseInt(value, 10);
    const parsedFallback = parseInt(fallback, 10);

    if (Number.isFinite(parsedValue) && parsedValue > 0) {
        return parsedValue;
    }

    return Number.isFinite(parsedFallback) && parsedFallback > 0 ? parsedFallback : 1;
}

const parseDeliveryDate = (value) => {
    const rawValue = String(value ?? "").trim();

    if (!rawValue || rawValue.toUpperCase() === "AUCUNE DATE") {
        return null;
    }

    const createDate = (dayValue, monthValue, yearValue) => {
        const day = parseInt(dayValue, 10);
        const month = parseInt(monthValue, 10) - 1;
        const year = yearValue.length === 2 ? 2000 + parseInt(yearValue, 10) : parseInt(yearValue, 10);
        const date = new Date(year, month, day);

        return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day ? date : null;
    };

    const compactDate = rawValue.match(/^(\d{2})(\d{2})(\d{2}|\d{4})$/);
    if (compactDate) {
        return createDate(compactDate[1], compactDate[2], compactDate[3]);
    }

    const separatedDate = rawValue.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2}|\d{4})$/);
    if (separatedDate) {
        return createDate(separatedDate[1], separatedDate[2], separatedDate[3]);
    }

    const parsedDate = new Date(rawValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

const formatDeliveryDate = (value) => {
    const parsedDate = parseDeliveryDate(value);
    return parsedDate ? format(parsedDate, "dd-MM-yyyy") : "";
}

const redirectToClientSelection = (navigate, redirectPath) => {
    sessionStorage.setItem('orders.after_client_redirect', redirectPath);
    return <Navigate to="/client" replace />;
}

const isOnlineCategory = (category) => {
    return category?.ligne === undefined || category?.ligne === null || parseInt(category.ligne) === 1;
}

const filterOnlineCategories = (cats = []) => {
    return cats.reduce((visibleCategories, category) => {
        const visibleChildren = filterOnlineCategories(category?.children ?? []);

        if (!isOnlineCategory(category) && visibleChildren.length === 0) {
            return visibleCategories;
        }

        visibleCategories.push({
            ...category,
            children: visibleChildren
        });

        return visibleCategories;
    }, []);
}

const getProductCategoryRef = (category) => {
    const categoryRef = String(category?.ref ?? "");
    return categoryRef.startsWith("ds-") ? categoryRef.replace("ds-", "") : categoryRef;
}

const categoryHasProduct = (category, products, productFilter = () => true) => {
    const categoryRef = getProductCategoryRef(category);

    return Boolean(categoryRef) && products.some((product) => (
        productMatchesCategory(product, categoryRef) && productFilter(product)
    ));
}

const filterCategoriesWithProducts = (cats = [], products = [], productFilter = () => true) => {
    return cats.reduce((visibleCategories, category) => {
        const visibleChildren = filterCategoriesWithProducts(category?.children ?? [], products, productFilter);

        if (!categoryHasProduct(category, products, productFilter) && visibleChildren.length === 0) {
            return visibleCategories;
        }

        visibleCategories.push({
            ...category,
            children: visibleChildren
        });

        return visibleCategories;
    }, []);
}

const flattenCategories = (cats = []) => {
    return cats.reduce((flatCategories, category) => {
        flatCategories.push(category);

        if (category?.children?.length) {
            flatCategories.push(...flattenCategories(category.children));
        }

        return flatCategories;
    }, []);
}

const filterCategories = (foundCategories, search) => {
    if (search?.categoriesRefs) {
        return filterSearchCategories(foundCategories, search.categoriesRefs);
    }

    return foundCategories.filter( (category) => (
        !category.destockOnly && ( ! search?.category ||
            category.ref == search.category.value ||
            (category.parent != null && category.parent.ref == search.category.value) ||
            (category.children.length > 0 && category.children.find((cc, idx) => {
                return (
                    cc.ref == search.category.value ||
                    (cc.children.length > 0 && cc.children.find(ccc => ccc.ref == search.category.value) != undefined)
                );
            }))
        )
    ))
}

const filterSearchCategories = (foundCategories, searchRefs) => {
    return foundCategories.filter( (category) => (
        searchRefs.includes(category.ref) ||
        (category.parent != null && searchRefs.includes(category.parent.ref)) ||
        (category.children.length > 0 && category.children.find((cc, idx) => {
            return (
                searchRefs.includes(cc.ref) ||
                (cc.children.length > 0 && cc.children.find(ccc => searchRefs.includes(ccc.ref)) != undefined)
            );
        }))
    ))
}

function Catalog( props ){

    //const categories = JSON.parse(localStorage.getItem('categories'))
    //const products = JSON.parse(localStorage.getItem('products'))

    const { getAll } = useIndexedDB('catalog');
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [ categorySearch, setCategorySearch ] = useState('')
    const [currentClient, setCurrentClient] = useState(getStoredCurrentClient);
    const [demoClientAccess, setDemoClientAccess] = useState(getStoredDemoClientAccess);
    const [productSearch, setProductSearch] = useState('');
    let location = useLocation();

    const [user, { updateFavoriteProducts }]  = useApi();
   
    sessionStorage.setItem('browse_mode', 'catalog')
    sessionStorage.removeItem('products.current_selected_page');
    sessionStorage.removeItem('products.current_selected_pagination');
    useEffect(() => {
        getAll().then(dataFromDB => {
            setProducts(dataFromDB.find(i => i.type == 'products')?.data ?? [])
            setCategories(dataFromDB.find(i => i.type == 'categories')?.data ?? [])
        });
        
        setCategorySearch(sessionStorage.getItem('catalog.search_category') ? JSON.parse(sessionStorage.getItem('catalog.search_category')) : '');
        setProductSearch(normalizeSearchTerm(sessionStorage.getItem('catalog.search_ref')));

        if (sessionStorage.getItem('orders.current_client')) {
            setCurrentClient(JSON.parse(sessionStorage.getItem('orders.current_client')));
        }
        if (sessionStorage.getItem('orders.demo_client_access')) {
            setDemoClientAccess(JSON.parse(sessionStorage.getItem('orders.demo_client_access')));
        }
    }, []);

    const navigate = useNavigate();

    let params = useParams()
    let url_params = params['*'].split('/')

    let categoryRef = url_params.length ? url_params[url_params.length-1] : null
    
    const [page, setPage ] = useState(0)    
   

    const recursiveFindCategory = (cats, ref) => {        

        for(let i = 0; i< cats.length; i++){
            
            if(cats[i].ref == ref)
            return cats[i]            
            
            const found = cats[i].children ? recursiveFindCategory(cats[i].children,ref) : false;
            
            if(found)
            return found;            
            
        };

        return false;
    }

    const recursiveFindProductCategory = (cats, ref) => {
        if (cats.ref == ref)
            return cats;

        const found = cats.parent ? recursiveFindProductCategory(cats.parent, ref) : false;
        return found;
    }

    const recursiveFindDestockCategory = (cats, title = "DESTOCKAGE") => {
        if (cats.title == title)
            return cats;

        const found = cats.parent ? recursiveFindDestockCategory(cats.parent, title) : false;
        return found;
    }

    const [ currentCategory, _setCurrentCategory ] = useState( categoryRef ? recursiveFindCategory(categories,categoryRef) : null)    

    const shouldRestoreCurrentCatalogPage = () => {
        const storedPath = sessionStorage.getItem('catalog.current_selected_category');
        return sessionStorage.getItem('backClicked') === 'true' &&
            storedPath &&
            storedPath.replaceAll('"', '') === location.pathname;
    }

    const setCurrentCategory = (value, resetPage = true) => {

        _setCurrentCategory(value);
        if (resetPage) {
            setPage(0)
        }

    }

    useEffect(() => {
        
        if(categoryRef && (!currentCategory ||  categoryRef != currentCategory.ref))
        setCurrentCategory(recursiveFindCategory(categories,categoryRef), !shouldRestoreCurrentCatalogPage())
        else
        if(!categoryRef && currentCategory)
        setCurrentCategory(null)

        
       


        
    })

    useEffect(() => {
        if (
            sessionStorage.getItem('backClicked') === 'true' && 
            (
                (sessionStorage.getItem('catalog.previous_selected_page') && sessionStorage.getItem('catalog.previous_selected_page') !== page) ||
                (sessionStorage.getItem('catalog.current_selected_page') && sessionStorage.getItem('catalog.current_selected_page') !== page)
            ) &&
            sessionStorage.getItem('catalog.current_selected_category')
        ) {
            let currentPathname = sessionStorage.getItem('catalog.current_selected_category').replaceAll('"', '');
            let selectedPageNum = parseInt(sessionStorage.getItem('catalog.current_selected_page'));
            let selectedPagination = parseInt(sessionStorage.getItem('catalog.current_selected_pagination'));

            if (currentPathname === location.pathname && selectedPagination === pagination) {
                setPage(selectedPageNum);
            } else if (sessionStorage.getItem('catalog.previous_selected_category')) {
                currentPathname = sessionStorage.getItem('catalog.previous_selected_category').replaceAll('"', '');
                selectedPageNum = parseInt(sessionStorage.getItem('catalog.previous_selected_page'));
                selectedPagination = parseInt(sessionStorage.getItem('catalog.previous_selected_pagination'));
                if (currentPathname === location.pathname && selectedPagination === pagination) {
                    setPage(selectedPageNum);
                }
            }
            setTimeout(() => {
                sessionStorage.setItem('backClicked', false);
            }, 300);
        }
    })

    let dsCategoryRef = (currentCategory && currentCategory.ref.includes('ds-')) ? currentCategory.ref.replace('ds-', '') : '';
    let foundCategories = filterOnlineCategories(currentCategory ? currentCategory.children : categories);

    if (currentClient?.excluded_categories && Object.keys(currentClient?.excluded_categories).length > 0) {
        foundCategories = foundCategories.filter((c) => {
            if (currentClient.excluded_categories.findIndex((ec) => String(ec.ref) === String(c.ref)) === -1) {
                return c;
            }
            return false;
        })
    }

    if (currentCategory?.ref === "destockage") {
        foundCategories = filterCategoriesWithProducts(foundCategories, products, (product) => isProductOnline(product) && product.destock === true);
    }

    const productSearchTerm = normalizeSearchTerm(productSearch);

    const productMatchesCatalogSearch = (product) => {
        if (!productSearchTerm) {
            return true;
        }

        const searchableFields = [product?.ref, product?.title, product?.material];

        return searchableFields.some((value) =>
            String(value ?? "").toLowerCase().includes(productSearchTerm.toLowerCase())
        );
    };

    const getCatalogProducts = () => {
        if (!currentCategory) {
            if (categoryRef) {
                return [];
            }

            return productSearchTerm
                ? products.filter((product) => isProductOnline(product) && productMatchesCatalogSearch(product))
                : [];
        }

        if (currentCategory.ref.includes('ds-')) {
            return products.filter((p) => (
                isProductOnline(p) &&
                p.categories &&
                recursiveFindProductCategory(p.categories, dsCategoryRef) &&
                p.destock == true &&
                productMatchesCatalogSearch(p)
            ));
        }

        const isDestockCategory = recursiveFindDestockCategory(currentCategory) !== false;
        const expectedDestockState = isDestockCategory;
        const matchingProducts = products.filter((p) => (
            productMatchesCategory(p, currentCategory.ref) &&
            isProductOnline(p) &&
            p.destock === expectedDestockState &&
            productMatchesCatalogSearch(p)
        ));

        if (matchingProducts.length || isDestockCategory) {
            return matchingProducts;
        }

        return products.filter((p) => (
            productMatchesCategory(p, currentCategory.ref) &&
            isProductOnline(p) &&
            p.destock === true &&
            productMatchesCatalogSearch(p)
        ));
    }

    let foundProducts = getCatalogProducts() 
    const filteredCategories = productSearchTerm ? [] : filterCategories(foundCategories, {'category':categorySearch })

    const displayMode = productSearchTerm ? "products" : (!currentCategory || (foundCategories && foundCategories.length) ? "categories" : "products")
    let totalResult = 0
    const [ pagination, setPagination ] =  useState(12)

    if(displayMode == "categories"){
        totalResult = filteredCategories.length
    }
    else
    if(displayMode == "products"){
        totalResult = foundProducts.length
    }
    const totalPages = Math.ceil(totalResult / pagination)

    const gotoPreviousPage = () => {

        let previousPage = page-1

        if(previousPage >= 0)
        handleCatalogPageChange( previousPage )
    }

    const gotoNextPage = () => {

        let nextPage = page+1;

        if(nextPage <  totalPages)
        handleCatalogPageChange( nextPage )
       

    }

    const isClientAllowedCategory = (category) => (
        !currentClient?.excluded_categories ||
        currentClient.excluded_categories.findIndex((ec) => String(ec.ref) === String(category.ref)) === -1
    );

    const getVisibleCatalogChildren = (category) => {
        let visibleChildren = filterOnlineCategories(category?.children ?? []);

        if (currentClient?.excluded_categories && Object.keys(currentClient?.excluded_categories).length > 0) {
            visibleChildren = visibleChildren.filter((childCategory) => isClientAllowedCategory(childCategory));
        }

        if (category?.ref === "destockage") {
            visibleChildren = filterCategoriesWithProducts(visibleChildren, products, (product) => isProductOnline(product) && product.destock === true);
        }

        return visibleChildren;
    }

    const getCatalogProductsForCategory = (category) => {
        if (!category) {
            return [];
        }

        const categoryRef = getProductCategoryRef(category);
        const isDestockCategory = recursiveFindDestockCategory(category) !== false;

        if (String(category?.ref ?? "").includes("ds-")) {
            return products.filter((p) => (
                isProductOnline(p) &&
                p.categories &&
                recursiveFindProductCategory(p.categories, categoryRef) &&
                p.destock == true &&
                productMatchesCatalogSearch(p)
            ));
        }

        const matchingProducts = products.filter((p) => (
            productMatchesCategory(p, categoryRef) &&
            isProductOnline(p) &&
            p.destock === isDestockCategory &&
            productMatchesCatalogSearch(p)
        ));

        if (matchingProducts.length || isDestockCategory) {
            return matchingProducts;
        }

        return products.filter((p) => (
            productMatchesCategory(p, categoryRef) &&
            isProductOnline(p) &&
            p.destock === true &&
            productMatchesCatalogSearch(p)
        ));
    }

    const isNavigableCatalogCategory = (category) => (
        isClientAllowedCategory(category) &&
        filterOnlineCategories([category]).length > 0 &&
        getVisibleCatalogChildren(category).length === 0 &&
        getCatalogProductsForCategory(category).length > 0
    );

    const getResolvedParentCategory = (category) => category?.parent ? recursiveFindCategory(categories, category.parent.ref) : null;

    const getNavigationRootCategory = (category) => {
        let rootCategory = category;
        let parentCategory = getResolvedParentCategory(rootCategory);

        while (parentCategory) {
            rootCategory = parentCategory;
            parentCategory = getResolvedParentCategory(rootCategory);
        }

        return rootCategory;
    }

    const findAdjacentCategory = (direction) => {
        if (!currentCategory) {
            return null;
        }

        const rootCategory = getNavigationRootCategory(currentCategory);
        const scopeCategories = rootCategory?.children?.length ? rootCategory.children : categories;
        const navigableCategories = flattenCategories(filterOnlineCategories(scopeCategories)).filter(isNavigableCatalogCategory);
        const currentIndex = navigableCategories.findIndex(c => c.ref === currentCategory.ref);

        if (currentIndex === -1) {
            return null;
        }

        return navigableCategories[currentIndex + direction] ?? null;
    };
    const previousCategory = findAdjacentCategory(-1);
    const nextCategory = findAdjacentCategory(1);

    const gotoPreviousCollection = (e) => {
        e.preventDefault();

        if(previousCategory)
        navigate('/catalog/'+previousCategory.ref, { replace: true });
    }

    const gotoNextCollection = (e) => {   
        e.preventDefault(); 

        if (nextCategory) {
            navigate('/catalog/'+nextCategory.ref, { replace: true });

            const swiper = document.querySelector('.swiper-catalog').swiper;
            swiper.activeIndex = 0;
        }
    }

    const pageItems = [
        displayMode === "products" && <li className='page-item' key='collection-backward'>
            <a href="#" onClick={ gotoPreviousCollection } className={'page-link' + ( !previousCategory ? ' opacity-50':'') }><i className='fa fa-step-backward'></i></a>
        </li>,
        <li className='page-item' key='page-backward'>
            <a href="#" onClick={ gotoPreviousPage } className={'page-link' + (page <= 0 ? ' opacity-50':'') }><i className='fa fa-backward'></i></a>
        </li>
    ]      
    
    for(let i = 0; i< totalPages; i++){
        pageItems.push(
        <li key={ 'page_item_' + i } className={ page == i ? 'page-item active' : 'page-item' }>
            <a href="#" onClick={ () => { handleCatalogPageChange(i); }  } className='page-link'>{ i+1 }</a>
        </li>)
    }    

    pageItems.push(
        <li className='page-item' key='page-forward'>
    <a href="#" onClick={ gotoNextPage } className={'page-link' + (page >= totalPages-1 ? ' opacity-50':'') }><i className='fa fa-forward'></i></a></li>,
    displayMode === "products" && <li className='page-item' key='collection-forward'>
        <a href="#" onClick={ gotoNextCollection } className={'page-link' + (! nextCategory ? ' opacity-50':'') }><i className='fa fa-step-forward'></i></a>
    </li>)

    const start = page*pagination
    const end = start + pagination
    let itemClassNames = 'col-lg-3';
    switch(pagination){

        case 6:
        itemClassNames = 'col-lg-4'
        break

        case 12:
        itemClassNames = 'col-lg-3'
        break



            
    }




    const getBreadcrumb = (cat) => {

        
        let cats = [cat]   
        
        let currentCat = cat

        while(currentCat.parent){

            cats.push(currentCat.parent)
            currentCat = currentCat.parent

        }

        

        cats = cats.reverse();

       

        let cats_link = cats.map((c, index) => 
            {
              
                return (
                    <Link className='text-white'  key={ index } to={ "/catalog/"+ c.ref } >{ c.title }</Link>
                )
            }            
        );

        let breadCrumb = <div className=''>
            <Link className='text-white'  key="root" to="/catalog" >{ strings.catalogue }</Link> &gt;
            { 
                
                cats_link.map( (link,index) => 
                    [
                        index > 0 ? " > " : '',
                        link
                    ]
                )
            }
        </div>
            
       

        return breadCrumb;
    }

    const handleChangePagination = (e) => {
        setPagination(parseInt(e.target.value));
        handleCatalogPageChange(0)
    }

    const handleCatalogCategorySearch = (value) => {

        sessionStorage.setItem('catalog.search_category', JSON.stringify(value))  
        setCategorySearch(value)

    }

    const handleProductSearch = (e) => {
        const inputValue = e.target.value;
        const searchTerm = normalizeSearchTerm(inputValue);
        sessionStorage.setItem('catalog.search_ref', searchTerm)
        setProductSearch(inputValue)
        setPage(0)
    }

    const handleProductSearchBlur = () => {
        setProductSearch(productSearchTerm)
    }

    const handleCatalogPageChange = (value) => {
        // store previous pagination info
        sessionStorage.setItem('catalog.previous_selected_category', sessionStorage.getItem('catalog.current_selected_category'))
        sessionStorage.setItem('catalog.previous_selected_page', sessionStorage.getItem('catalog.current_selected_page'))
        sessionStorage.setItem('catalog.previous_selected_pagination', sessionStorage.getItem('catalog.current_selected_pagination'))

        // store current pagination info
        sessionStorage.setItem('catalog.current_selected_category', JSON.stringify(location.pathname))  
        sessionStorage.setItem('catalog.current_selected_page', JSON.stringify(value))  
        sessionStorage.setItem('catalog.current_selected_pagination', JSON.stringify(pagination))  
        setPage(value)
    }

    const [{ selectStyles }] = useCustomFields()
    
    const categoriesOptions =  []
    
    const recursiveGetCategory = (cats, level = -1) => {

        level++

        const spacer =  level  ? "---".repeat(level)+" " : ''

        cats.forEach(c => {
            let excludeCategory = false;
            if (currentClient?.excluded_categories && Object.keys(currentClient?.excluded_categories).length > 0) {
                if (currentClient.excluded_categories.findIndex((ec) => String(ec.ref) === String(c.ref)) !== -1) {
                    excludeCategory = true;
                }
            }

            if (!excludeCategory) {
                categoriesOptions.push({ label:spacer+c.title, value:c.ref, parent:c.parent })
            }
            if(c.children.length)
            recursiveGetCategory(c.children,level)
        }) 
        
    }

    if (categories.length === foundCategories.length) {
        recursiveGetCategory(categories);
    } else {
        recursiveGetCategory(foundCategories);
    }


    // Create array with slides
    const slides = Array.from({ length: totalPages }).map(
        (el, index) => `Slide ${index + 1}`
    );

    const [ zoomImage, setZoomImage ] = useState(null);

    const toggleProductFavorite = (product) => {
        const updatedClient = {
            ...currentClient,
            favorite_products: toggleFavoriteProductRef(currentClient?.favorite_products, product.ref)
        };

        sessionStorage.setItem('orders.current_client', JSON.stringify(updatedClient));
        setCurrentClient(updatedClient);
        updateFavoriteProducts(updatedClient);
    }

    const showSearchRefInput = true;
    const catalogSearchBar = (
        <div className='search-bar mb-3 container-xxl'>
            <div className="row justify-content-end">
                {
                    showSearchRefInput ?
                    <div className='col-lg-2'>
                        <input type="text" name="ref" className="form-control" onChange={ handleProductSearch } onBlur={ handleProductSearchBlur } value={ productSearch } placeholder='REF' />
                    </div>
                    : ''
                }
                <div className='col-lg-3' style={ {zIndex:2 } }>
                    <Select styles={ selectStyles } onChange={ handleCatalogCategorySearch } value={ categorySearch } isClearable={ true } name="collection" className="custom-select" options={ categoriesOptions } />
                </div>
            </div>
        </div>
    );
   
    return (
        currentClient === null && !demoClientAccess
        ? redirectToClientSelection(navigate, location.pathname)
        : <div className="products h-100 d-flex flex-column justify-content-between">
            <Ariane showSearch>
                <h1 className='titre text-center flex-grow-1 fs-4'>{ currentCategory ? getBreadcrumb(currentCategory) : strings.catalogue_produit }</h1>
            </Ariane>
            { catalogSearchBar }
            { displayMode === "categories" 
                ?
                <div className='categories-list my-4 container-xxl'>
                    <Swiper pagination={false} modules={[Pagination]} onSlideChange={(swiper) => handleCatalogPageChange(swiper.activeIndex)} className='swiper-catalog' preventClicks={true} preventClicksPropagation={true} threshold={10} touchRatio={0.5}>
                        {slides.map((slideContent, index) => (
                            <SwiperSlide key={slideContent}>
                                <div className='row'>
                                    { filteredCategories.slice(start,end).map( cat => (
                                        <div className={ itemClassNames+' mb-4' } key={ 'cat_'+cat.ref }>
                                            <Link to={ cat.ref } className='btn btn-primary btn-category p-0 w-100'>
                                                <div className='ratio ratio-16x9'>
                                                    { cat.image ? <img src={ cat.image } className="img img-cover" /> : ( products.find(p => isProductOnline(p) && p.categories?.ref == cat.ref ) ? <img src={ products.find((p) => isProductOnline(p) && p.categories?.ref == cat.ref )?.images[0] } className="img img-cover" /> : <img src={ notfound } className="img img-cover" /> ) }
                                                </div>                                
                                                <div className='caption'>
                                                    { cat.title}
                                                </div>
                                                { currentCategory && currentCategory.ref === 'destockage' && <span className="badge rounded-pill text-bg-warning position-absolute top-0 end-0  mt-1 me-1">{ strings.destock }</span>}  
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
                : 
                <Swiper observer={true} pagination={false} modules={[Pagination]} onSlideChange={(swiper) => handleCatalogPageChange(swiper.activeIndex)} className='swiper-catalog' preventClicks={true} preventClicksPropagation={true} threshold={10} touchRatio={0.5}>
                    {slides.map((slideContent, index) => (
                        <SwiperSlide key={slideContent}>
                            <div className='products-list my-4 container-xxl p-0'>
                                <div className='row'>
                                    {
                                        foundProducts.slice(start,end).map( product => (
                                            <ProductItem product={ product } className={ itemClassNames } key={ product.ref } withStock={ false } futureStockValue={ 0 } setZoomImage={setZoomImage} toggleProductFavorite={toggleProductFavorite} selectedClient={currentClient} />
                                        ))
                                    }
                                </div>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            }

            {
                zoomImage
                ? <div className="img-modal">
                    <div className="img-modal-content">
                        <span className="img-close" onClick={ () => setZoomImage(null) }>&times;</span>
                        <img src={ zoomImage } className="img img-contain" alt='' />
                    </div>
                </div>
                : <></>
            }

            <BottomNavigation>
                <Link to="/" className='fs-1 text-white col-3 text-start'>
                    <i className='fa fa-home'></i>
                </Link>
                <div className="col-6 d-flex align-items-center justify-content-center">
                    <ul className='pagination justify-content-center m-0 pagination me-3'>
                        { pageItems }
                    </ul>
                    <div className="d-flex justify-content-center">
                        <div className="col-auto">
                            <label className="col-form-label">{ strings.afficher }</label>
                        </div>
                        <div className="col-auto">
                            <select name="product_num" value={ pagination } onChange={ handleChangePagination } className="ms-3 form-select">                        
                              
                                <option value="6">6 { strings.elements}</option>
                                <option value="12">12 { strings.elements}</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className='col-3'>
                    <MiniCart />
                </div>
            </BottomNavigation>
        </div>
    )
}

export default Catalog;

const filterProducts = (products, search) => {
    const refSearch = normalizeSearchTerm(search?.ref);

    const showDestockProduct = search?.destock ?? false;
    return products.filter( (product) => (
        isProductOnline(product)
        &&
        ( ! refSearch || 
            (
                product.ref.toLowerCase().includes(refSearch.toLowerCase()) 
                || 
                product.title.toLowerCase().includes(refSearch.toLowerCase())
                ||
                String(product.material ?? '').toLowerCase().includes(refSearch.toLowerCase())
            )
        )
        &&
        ( product.destock === showDestockProduct )
        &&
        ( ! search?.category ||
            productMatchesCategory(product, search.category.value)
        )
        &&
        ( ! search?.currentClient?.excluded_categories ||
            search?.currentClient.excluded_categories.findIndex((ec) => String(ec.ref) === String(product.categories?.ref)) === -1
        )
    ))
}


function ProductList(){

    //const products = JSON.parse(localStorage.getItem('products'))
    //const categories = JSON.parse(localStorage.getItem('categories'))
    sessionStorage.setItem('browse_mode', 'list')
    sessionStorage.removeItem('catalog.current_selected_page');
    sessionStorage.removeItem('catalog.previous_selected_page');

    const { getAll } = useIndexedDB('catalog');
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [ orders, setOrders] = useState([])
    const [ productSearch, setProductSearch ] = useState('')
    const [ destockSearch, setDestockSearch ] = useState(false)
    const [ categorySearch, setCategorySearch ] = useState('')
    const [page, setPage ] = useState(1)    
    const [ pagination, setPagination ] =  useState(12)
    const [currentClient, setCurrentClient] = useState(getStoredCurrentClient);
    const [demoClientAccess, setDemoClientAccess] = useState(getStoredDemoClientAccess);
    let totalResult = 0
    let navigate = useNavigate();
    let location = useLocation();

    const [user, { updateFavoriteProducts }]  = useApi();

    useEffect(() => {
        getAll().then(dataFromDB => {
            setProducts(dataFromDB.find(i => i.type == 'products')?.data ?? [])
            setCategories(dataFromDB.find(i => i.type == 'categories')?.data ?? [])
            setOrders(dataFromDB.find(i => i.type === 'orders')?.data ?? [])
        });
        setProductSearch(normalizeSearchTerm(sessionStorage.getItem('products.search_ref')));
        setDestockSearch(sessionStorage.getItem('products.search_destock') === 'true' ? true:false);
        
        setCategorySearch(sessionStorage.getItem('products.search_category') ? JSON.parse(sessionStorage.getItem('products.search_category')) : '');
        
        
        if (
            sessionStorage.getItem('backClicked') === 'true' && 
            sessionStorage.getItem('products.current_selected_page') &&
            sessionStorage.getItem('products.current_selected_page') !== page
        ) {
            let selectedPageNum = parseInt(sessionStorage.getItem('products.current_selected_page'));
            let selectedPagination = parseInt(sessionStorage.getItem('products.current_selected_pagination'));
            if (selectedPagination === pagination) {
                setPage(selectedPageNum);
            }
            setTimeout(() => {
                sessionStorage.setItem('backClicked', false);
            }, 300);
        }

        if (sessionStorage.getItem('orders.current_client')) {
            setCurrentClient(JSON.parse(sessionStorage.getItem('orders.current_client')));
        }
        if (sessionStorage.getItem('orders.demo_client_access')) {
            setDemoClientAccess(JSON.parse(sessionStorage.getItem('orders.demo_client_access')));
        }
    }, []);

    const { addItem, inCart, getItem, updateItemQuantity, removeItem } = useCart()

    const getProductPackUnit = (id, defaultVal) => {
        const storedPackUnit = sessionStorage.getItem('products.productPackUnit.' + id);
        return normalizePositiveInteger(storedPackUnit, defaultVal);
    }

    const handleAddToCart = (product) => {

        const price = !product.destock && isDiscounted(1) ? getDiscountPrice(product.regular_price) : product.regular_price ?? 0;
        const discount = getClientGeneralDiscount();

        inCart(product.ref) ? updateItemQuantity(getItem(product.ref).id, getItem(product.ref).quantity + getProductPackUnit(product.ref, product.pack_unit)) :  addItem({ id:product.ref, price: price, name: product.title,  image: product.images?.length ? product.images : null, pack_unit : getProductPackUnit(product.ref, product.pack_unit), discount: discount, regular_price: product.regular_price, destock: product.destock}, getProductPackUnit(product.ref, product.pack_unit))
    }
    
    const paginationComponentOptions = {
        rowsPerPageText: strings.articles_page,
        rangeSeparatorText: strings.articles_of,
        selectAllRowsItem: true,
        selectAllRowsItemText: strings.articles_all,
        
    };
    const viewProduct = (ref) => {
        navigate('/product/'+ref)
    }

  

    const handleProductSearch = (e) => {
        const inputValue = e.target.value;
        const searchTerm = normalizeSearchTerm(inputValue);

        sessionStorage.setItem('products.search_ref', searchTerm)

        setProductSearch(inputValue)
        handleProductsPageChange(1)

    }

    const handleDestockSearch = (e) => {

        sessionStorage.setItem('products.search_destock',!destockSearch)

        setDestockSearch(!destockSearch)
        handleProductsPageChange(1)

    }

    const handleCategorySearch = (value) => {

        sessionStorage.setItem('products.search_category', JSON.stringify(value))  
        setCategorySearch(value)
        handleProductsPageChange(1)

    }

    const filteredProducts = filterProducts(
        products,
        { 'ref': productSearch, 'category':categorySearch, 'destock': destockSearch, 'currentClient': currentClient }
    );

    const [{ selectStyles }] = useCustomFields()
    
    const categoriesOptions =  []
    
    const recursiveGetCategory = (cats, level = -1) => {

        level++

        const spacer =  level  ? "---".repeat(level)+" " : ''

        cats.forEach(c => {
            let excludeCategory = false;
            if (currentClient?.excluded_categories && Object.keys(currentClient?.excluded_categories).length > 0) {
                if (currentClient.excluded_categories.findIndex((ec) => String(ec.ref) === String(c.ref)) !== -1) {
                    excludeCategory = true;
                }
            }

            if (!excludeCategory) {
                categoriesOptions.push({ label:spacer+c.title, value:c.ref })
            }
            if(c.children.length)
            recursiveGetCategory(c.children,level)
        }) 
        
    }

    recursiveGetCategory(categories)

    totalResult = filteredProducts.length
    const totalPages = Math.ceil(totalResult / pagination)

    const gotoPreviousPage = () => {

        let previousPage = page-1
        
        if(previousPage >= 1)
        handleProductsPageChange( previousPage )
    }

    const gotoNextPage = () => {

        let nextPage = page+1;
        
        if(nextPage <=  totalPages)
        handleProductsPageChange( nextPage )
       

    }

    const pageItems = [
        <li className='page-item' key='page-backward'>
            <a href="#" onClick={ gotoPreviousPage } className={'page-link' + (page <= 1 ? ' opacity-50':'') }><i className='fa fa-backward'></i></a>
        </li>
    ];

    const getPaginationButtons = () => {
        if (totalPages <= 4) {
            const pages = [...new Array(totalPages)].map((_, index) =>
                (index + 1).toString()
            );
            return pages;
        }

        const pages = ['1'];

        if (page >= 4) {
            pages.push('...');
        }

        if (page + 1 > totalPages) {
            pages.push((page - 2).toString());
        }

        if (page - 1 > 1) {
            pages.push((page - 1).toString());
        }

        if (page !== 1) {
            pages.push(page.toString());
        }

        if (page + 1 <= totalPages) {
            pages.push((page + 1).toString());
        }

        if (page - 1 <= 0) {
            pages.push((page + 2).toString());
        }

        if (page + 2 < totalPages) {
            pages.push('...');
        }

        if (page + 1 < totalPages) {
            pages.push(totalPages.toString());
        }

        return pages;
    };

    const paginationButtons = getPaginationButtons();
    paginationButtons.forEach((val, idx) => {
        if (val === '...') {
            pageItems.push(
            <li key={ 'page_item_dd' + idx } className={ 'page-item' }>
                <a href="#" className='page-link'>...</a>
            </li>);
        } else {
            let i = parseInt(val);
            pageItems.push(
            <li key={ 'page_item_' + i } className={ page == i ? 'page-item active' : 'page-item' }>
                <a href="#" onClick={ () => { handleProductsPageChange(i);} } className='page-link'>{ i }</a>
            </li>);
        }
    });

    pageItems.push(
        <li className='page-item' key='page-forward'>
        <a href="#" onClick={ gotoNextPage } className={'page-link' + (page >= totalPages ? ' opacity-50':'') }><i className='fa fa-forward'></i></a></li>
    );

    const start = (page - 1)*pagination
    const end = start + pagination
    let itemClassNames = 'col-lg-3';
    switch(pagination){
        case 6:
            itemClassNames = 'col-lg-4'
            break
        case 12:
        default:
            itemClassNames = 'col-lg-3'
            break
    }
    const handleChangePagination = (e) => {
        setPagination(parseInt(e.target.value));
        handleProductsPageChange(1)
    }

    const handleProductsPageChange = (value) => {
        
        sessionStorage.setItem('products.current_selected_page', JSON.stringify(value));
        sessionStorage.setItem('products.current_selected_pagination', JSON.stringify(pagination));
        setPage(value);
    }

    const recursiveFindProductQty = (ref, statuses) => {
        let count = 0;
        const statusList = Array.isArray(statuses) ? statuses : [statuses];
        let filteredOrders = orders.filter(o => statusList.includes(parseInt(o.status)));
        for (const key in filteredOrders) {
            if (Object.prototype.hasOwnProperty.call(filteredOrders, key)) {
                const order = filteredOrders[key];
                for (const k in order.items) {
                    if (Object.prototype.hasOwnProperty.call(order.items, k)) {
                        const orderItem = order.items[k];
                        const orderItemRef = orderItem.ref ?? orderItem.id;
                        if (String(orderItemRef) === String(ref)) {
                            count += parseFloat(orderItem.quantity) || 0;
                        }
                    }
                }
            }
        }
        return count;
    }
    
    const handleFutureStocks = (ref, defaultStockValue) => {
        if (ref) {
            let productQtyFromOrders = recursiveFindProductQty(ref, [1, 2, 3]);

            let futureStock = (parseFloat(defaultStockValue) || 0) - productQtyFromOrders;
            return futureStock > 0 ? futureStock : 0;
        }
        return parseFloat(defaultStockValue) || 0;
    }

    const [ zoomImage, setZoomImage ] = useState(null);

    const toggleProductFavorite = (product) => {
        const updatedClient = {
            ...currentClient,
            favorite_products: toggleFavoriteProductRef(currentClient?.favorite_products, product.ref)
        };

        sessionStorage.setItem('orders.current_client', JSON.stringify(updatedClient));
        setCurrentClient(updatedClient);
        updateFavoriteProducts(updatedClient);
    }

    return ( 
        currentClient === null && !demoClientAccess
        ? redirectToClientSelection(navigate, location.pathname)
        : <div className="products h-100 d-flex flex-column justify-content-between">
            <Ariane showSearch>
                <h1 className='titre text-center flex-grow-1'>{ strings.liste_articles}</h1>
            </Ariane>            
            <div className='products-list my-4 container-xxl'>
                <div className='search-bar mb-3'>
                    <div className="row justify-content-end">
                        <div className='col-lg-2'>
                            <input type="text" name="ref" className="form-control" onChange={ handleProductSearch } value={ productSearch } placeholder='REF' />                        
                        </div>
                        <div className='col-lg-2'>
                            <div className='form-check text-start'>
                                <input type="checkbox" name="destock" id="destock" className="form-check-input" onChange={ handleDestockSearch } checked={ destockSearch }  /> 
                                <label className='form-check-label' htmlFor='destock'>
                                    { strings.destock }
                                </label>
                            </div>             
                        </div>
                        <div className='col-lg-3' style={ {zIndex:2}}>
                            <Select styles={ selectStyles } onChange={ handleCategorySearch } value={ categorySearch } isClearable={ true } name="collection" className="custom-select" options={ categoriesOptions } />
                        </div>
                        <div className='col-lg-2'>
                            <button type='submit' className='btn btn-primary w-100 text-white'>
                                { strings.rechercher}
                            </button>                     
                        </div>
                    </div>    
                </div>   
                
                <div className='products-list my-4 container-xxl p-0'>
                    <div className='row'>
                        {
                            filteredProducts.slice(start,end).map( product => (
                                <ProductItem product={ product } className={ itemClassNames } key={ product.ref } withStock={ true } futureStockValue={ product.future_stock ?? handleFutureStocks(product.ref, product.stock) }  setZoomImage={setZoomImage} toggleProductFavorite={toggleProductFavorite} selectedClient={currentClient} />
                            ))
                        }
                    </div>
                </div>
            </div>

            {
                zoomImage
                ? <div className="img-modal">
                    <div className="img-modal-content">
                        <span className="img-close" onClick={ () => setZoomImage(null) }>&times;</span>
                        <img src={ zoomImage } className="img img-contain" alt='' />
                    </div>
                </div>
                : <></>
            }

            <BottomNavigation>
                <Link to="/" className='fs-1 text-white col-2 text-start'>
                    <i className='fa fa-home'></i>
                </Link>
                <div className="col-8 d-flex align-items-center justify-content-center" style={ {zIndex:2}}>
                    <ul className='pagination justify-content-center m-0 pagination me-3'>
                        { pageItems }
                    </ul>
                    <div className="d-flex justify-content-center">
                        <div className="col-auto">
                            <label className="col-form-label">{ strings.afficher }</label>
                        </div>
                        <div className="col-auto">
                            <select name="product_num" value={ pagination } onChange={ handleChangePagination } className="ms-3 form-select">                        
                              
                                <option value="6">6 { strings.elements}</option>
                                <option value="12">12 { strings.elements}</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className='col-2'>
                    <MiniCart />
                </div>
            </BottomNavigation>
        </div> 
    )
}

function ProductItem(props){

    const { product, withStock, futureStockValue, setZoomImage, toggleProductFavorite, selectedClient, ...rest } = props
    const { items, addItem, inCart, getItem, updateItem, updateItemQuantity, removeItem } = useCart()
   
   
    const [ isInCart, setIsInCart ] = useState(inCart(product.ref)); 

    const [toggled, setToggled] = useState(false)
   
    const getProductPackUnit = (id, defaultVal) => {
        const storedPackUnit = sessionStorage.getItem('products.productPackUnit.' + id);
        return normalizePositiveInteger(storedPackUnit, defaultVal);
    }

    const handleAddItem = () => {
        const qty = getProductPackUnit(product.ref, product.pack_unit);
        const cartPrice = getFinalDiscountPrice(product, qty, [], false);
        const cartDiscount = getFinalDiscountPercentage(product, qty, [], false);

        addItem({
            id: product.ref,
            regular_price: product.regular_price,
            price: cartPrice,
            discount: cartDiscount,
            name: product.title,
            image: product.images?.length ? product.images : null,
            pack_unit: getProductPackUnit(product.ref, product.pack_unit),
            destock: product.destock
        }, qty);
        setIsInCart(true);
        setToggled(true);
    }
   
    const handleUpdateQuantity = (newQty) => {
        if(inCart(product.ref)){
            
            if(newQty){
                updateItemQuantity(item.id, newQty);
                setIsInCart(true)
            }
            else{
                removeItem(item.id);
                setIsInCart(false)
                setToggled(false)
            }
                
        }
        
        removeFreeItemAndCode(product, newQty);
    }

    const removeFreeItemAndCode = (row, qty = null) => {
        // check if base discount product is still in cart, if not remove free-item
        const promoCodeApplied = JSON.parse(sessionStorage.getItem('orders.applied_promo'));
        if (promoCodeApplied) {
            let clearFreeItems = false;
            let removeAppliedPromo = false;

            const discountType = promoCodeApplied.type;
            // check if free-item discount code
            if (discountType == 5) {
                // check if base discount-product is being removed
                if (row.ref === promoCodeApplied.productId) {
                    if (qty !== null) {
                        const promoMinQty = promoCodeApplied.minQuantity ?? 2;
                        const prodQty = qty / row.pack_unit;
                        if (prodQty < promoMinQty) {
                            clearFreeItems = true;
                            removeAppliedPromo = true;
                        } else {
                            applyPromoDiscount(promoCodeApplied);
                        }
                    } else {
                        clearFreeItems = true;
                        removeAppliedPromo = true;
                    }
                }
            }

            if (clearFreeItems) {
                // check and remove any free item if invalid promo
                const toRemoveFreeItems = items.filter((i) => i.freeItem === true);
                for (const k in toRemoveFreeItems) {
                    if (!Object.hasOwn(toRemoveFreeItems, k)) continue;
                    const itemToRemove = toRemoveFreeItems[k];

                    removeItem(itemToRemove.id);
                    removeAppliedPromo = true;
                }
            }

            if (removeAppliedPromo) {
                sessionStorage.removeItem('orders.applied_promo');
            }
        }
    }

    const applyPromoDiscount = (promoInfo) => {
        if (promoInfo === null) {
            sessionStorage.removeItem('orders.applied_promo');
        } else {
            sessionStorage.setItem('orders.applied_promo', JSON.stringify(promoInfo));
        }

        let clearFreeItems = false;
        let discountItemsCount = 0;
        const discountType = getAppliedDiscountType();
        if (discountType > 0) {
            for (const key in items) {
                if (Object.prototype.hasOwnProperty.call(items, key)) {
                    const product = items[key];

                    // ignore free item
                    if (product.freeItem) {
                        continue;
                    }

                    // TYPE_BUYX_GET1_FREE
                    if (discountType == 5) {
                        const freeProduct = getFreeProductBaseData(product, product.quantity, [], false);
                        const freeItemRef = 'F' + product.id;
                        // skip if not a valid discount product
                        if (freeProduct == null) {
                            // remove free item already added
                            const toRemoveFreeItem = items.find((i) => i.id === freeItemRef && i.freeItem == true);
                            if (toRemoveFreeItem !== undefined) {
                                removeItem(toRemoveFreeItem.id);
                            }
                            continue;
                        }
                        discountItemsCount++;

                        const prodPackUnit = getProductPackUnit(product.ref, product.pack_unit);
                        const prodPackQty = product.quantity / prodPackUnit;

                        const existingFreeItem = items.find((i) => i.id === freeItemRef && i.freeItem == true);

                        let promoMinQty = getFreeProductMinQty();
                        let freeQtyConst = Math.floor(prodPackQty / promoMinQty);
                        let freeItemQty = prodPackUnit * freeQtyConst;

                        if (existingFreeItem !== undefined) {
                            updateItem(getItem(freeItemRef).id, {
                                quantity: freeItemQty,
                                price: 0,
                                regular_price: freeProduct.regular_price,
                                discount: 0
                            })
                        } else {
                            addItem({
                                id: freeItemRef,
                                price: 0,
                                name: freeProduct.title,
                                image: freeProduct.images.length ? freeProduct.images : null,
                                pack_unit: prodPackUnit,
                                regular_price: freeProduct.regular_price,
                                discount: 0,
                                destock: freeProduct.destock,
                                freeItem: true,
                            }, freeItemQty);
                        }
                    } else {
                        clearFreeItems = true;
                        const discountPrice = getFinalDiscountPrice(product, product.quantity, [], false);
                        const discountPercentage = getFinalDiscountPercentage(product, product.quantity, [], false);

                        if (discountPercentage > 0) {
                            discountItemsCount++;
                        }
                        updateItem(
                            product.id, {
                                quantity: product.quantity,
                                price: discountPrice,
                                regular_price: product.regular_price,
                                discount: discountPercentage
                            }
                        );
                    }
                }
            }
        }

        if (clearFreeItems) {
            // check and remove any free item if not BXG1 promo
            const toRemoveFreeItems = items.filter((i) => i.freeItem === true);
            for (const k in toRemoveFreeItems) {
                if (!Object.hasOwn(toRemoveFreeItems, k)) continue;
                const itemToRemove = toRemoveFreeItems[k];

                removeItem(itemToRemove.id);
            }
        }
    
        if (promoInfo !== null && discountItemsCount === 0) {
            applyPromoDiscount(null);
        }
    }
    
    const item = getItem(product.ref)
    const price = !product.destock && isDiscounted(1) ? getDiscountPrice(product.regular_price) : product.regular_price ?? 0

    const handleZoomImage = (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        props.setZoomImage(product.images);
    }

    let canShowZoomIcon = true;
    const showZoomIcon = canShowZoomIcon && product.images?.length > 0;

    let showFavoriteIcon = typeof toggleProductFavorite === 'function';

    let clientFavoriteProducts = getFavoriteProductRefs(selectedClient?.favorite_products);
    let isFavoriteProduct = clientFavoriteProducts.includes(String(product.ref));

    return (
        <div {...rest} >
            <div className='item'>
                <div className='position-relative border-grey'>
                    <Link to={ "/product/" + product.ref } className='ratio bg-white ratio-16x9'>
                        { product.images?.length > 0
                            ? 
                            <img src={ product.images } className="img img-contain" />
                            :
                            <img src={ notfound } className="img img-contain" />
                        } 
                                         
                    </Link>                
                    {
                        showZoomIcon ?
                        <button type="button" className='img-zoom-button' onMouseEnter={handleZoomImage} onClick={handleZoomImage} onTouchEnd={handleZoomImage} aria-label="Agrandir l'image">
                            <i className="fa fa-search-plus"></i>
                        </button>
                        : <></>
                    }

                    {
                        showFavoriteIcon ? 
                            <button type="button" onMouseDown={ (event) => event.stopPropagation() } onClick={ () => props.toggleProductFavorite(product) } className='btn btn-primary add-favorites text-white'>
                                <i className='fa fa-heart' style={{ color: isFavoriteProduct ? '#B2022F' : '#fff' }}></i>
                            </button>
                        : <></>
                    }

                    { ! isInCart 
                    ? 
                        <button type="button" onClick={ handleAddItem } className='btn btn-primary add-to-cart text-white' style={ {zIndex:0 } }>
                            <i className='fa fa-shopping-bag'></i>
                        </button>
                    :
                        <QuantitySelector updateQuantity={ handleUpdateQuantity } toggled={ toggled } collapseAfterChange={ true } onCollapse={ () => setToggled(false) } packUnit={ getProductPackUnit(product.ref, product.pack_unit) }  quantity={ item ? item.quantity : getProductPackUnit(product.ref, product.pack_unit) } />
                    }
                    { product.destock && <span className="badge rounded-pill text-bg-warning position-absolute top-0 end-0  mt-1 me-1">{ strings.destock }</span>}  
                </div>
                <div className='price d-flex text-primary p-1 justify-content-between'>
                        <div>
							{ product.ref }
					 		{ withStock ? strings.future_stock_article + futureStockValue : ''}
					 	</div>
                        <div>
                            { 
                            parseFloat(price) !== parseFloat(product.regular_price) 
                                ? <><s className='me-1 small'>{ formatCurrency(product.regular_price) }</s><span className=''>{ formatCurrency(price) }</span></> 
                                : formatCurrency(price) 
                            }
                        </div>
                </div>
                
            </div>
        </div>
    )
}



function ProductSingle(props){

    const { items, addItem, inCart, updateItem, getItem, removeItem } = useCart();
    let { productRef } = useParams()
    
    let [product,setProduct] = useState(null)
    let [categoryProducts, setCategoryProducts] = useState([])
    const [ orders, setOrders] = useState([])
    const [allProducts, setAllProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    //{images:[notfound], ref:productRef,title:'Produit non trouvé',stock:0,regular_price:0,description:'Non trouvé', prices: []}
    
    const [mode, setMode] = useState("out-in");
    const [slideState, setSlideState] = useState(true);
    const [slideFadeClass, setSlideFadeClass] = useState('fade');
    const [currentClient, setCurrentClient] = useState(getStoredCurrentClient);
    const [demoClientAccess, setDemoClientAccess] = useState(getStoredDemoClientAccess);
    const slideRef = useRef();
    const productSwipeRef = useRef(null);

    const navigate = useNavigate()
    const location = useLocation()

    // let products = localStorage.getItem('products') 
    // if(products){
    //     let allProducts = JSON.parse(products)
    //     let foundProduct = allProducts.find((p) => p.ref == productRef)
    //     if(foundProduct)
    //     product = foundProduct
    // } 

    
    const { getAll } = useIndexedDB('catalog');
    useEffect(() => {
        getAll().then(dataFromDB => {
            let foundProducts = dataFromDB.find(i => i.type == 'products')?.data ?? []            
            let foundProduct = foundProducts.find((p) => p.ref == productRef && isProductOnline(p)) 
            let foundOrders = dataFromDB.find(i => i.type === 'orders')?.data ?? [];
            let foundCategories = dataFromDB.find(i => i.type == 'categories')?.data ?? [];
            setOrders(foundOrders)
            setAllProducts(foundProducts);
            setCategories(foundCategories);

            if (!foundProduct) {
                setProduct(null);
                setCategoryProducts([]);
                return;
            }
            
            let prices = [...(foundProduct.prices ?? [])];

            for(var i=0; i< prices.length-1; i++)
            {
                prices[i].amount = prices[i+1].amount
                prices[i].min = prices[i].max+1
                prices[i].max = false
                
            }
            prices.pop()

            foundProduct.prices = prices;

            setProduct(foundProduct);

            if(sessionStorage.getItem('browse_mode') == 'list')
            {
                let categorySearch = JSON.parse(sessionStorage.getItem('products.search_category'))
                let productSearch =  sessionStorage.getItem('products.search_ref')
                let destockSearch =  sessionStorage.getItem('products.search_destock') === "true" ? true : false 

                const filteredProducts = filterProducts(foundProducts,{ 'ref': productSearch, 'category':categorySearch, 'destock':destockSearch })
                
                setCategoryProducts(filteredProducts)
            }
            
            else
            setCategoryProducts(foundProducts.filter(p => isProductOnline(p) && productMatchesCategory(p, foundProduct.categories?.ref)))

            //console.log(foundProduct)
            
            if(foundProduct){
                if(inCart(foundProduct.ref))
                {
                    setQty( getItem(foundProduct.ref).quantity )
                    setDiscount( getItem(foundProduct.ref).discount )
                }
                else
                {                    
                    setQty(getProductPackUnit(foundProduct.ref, foundProduct.pack_unit))
                }
            }
        });

        if (sessionStorage.getItem('orders.current_client')) {
            setCurrentClient(JSON.parse(sessionStorage.getItem('orders.current_client')));
        }
        if (sessionStorage.getItem('orders.demo_client_access')) {
            setDemoClientAccess(JSON.parse(sessionStorage.getItem('orders.demo_client_access')));
        }
    }, [navigate, productRef]);
    

    const [qty, setQty ] = useState(1)
    const [ discount, setDiscount ] = useState()
    const price = !product?.destock && discount && isDiscounted(qty, discount) ? getDiscountPrice(product?.regular_price, discount.amount) : product?.regular_price ?? 0
    const total = qty * price
    const [ min, setMin ] = useState(false)
    const [ max, setMax ] = useState(false)
    const [editedPackUnit, setEditedPackUnit] = useState(product?.pack_unit ?? 1)
    const [showPackUnitEdit, setShowPackUnitEdit] = useState(false)
    const [ initDefaultPack, setInitDefaultPack ] = useState(false)
  
    const swiperRef = useRef();

    useEffect(() => {
        if (currentClient !== null) {
            setDiscount(getClientGeneralDiscount());
        }
    }, [currentClient])

    if (product && product.pack_unit && editedPackUnit !== product.pack_unit && !initDefaultPack) {
        setEditedPackUnit(product.pack_unit);
        setInitDefaultPack(true);
    }
    

    const handleQtyChange = (qty) => {
        setQty(qty);
        removeFreeItemAndCode(product, qty);
    }

    const getProductPackUnit = (id, defaultVal) => {
        const storedPackUnit = sessionStorage.getItem('products.productPackUnit.' + id);
        return normalizePositiveInteger(storedPackUnit, defaultVal);
    }
    const toggleProductUnitEdit = () => {
        if (product && product.ref) {
            setShowPackUnitEdit(!showPackUnitEdit);
        }
    }
    const handleProductPackUnitOverride = (newPackUnit) => {
        const normalizedPackUnit = normalizePositiveInteger(newPackUnit, product?.sub_packaging ?? product?.pack_unit ?? 1);
        setEditedPackUnit(normalizedPackUnit)
        sessionStorage.setItem('products.productPackUnit.' + product.ref, normalizedPackUnit)

        let newQty = parseInt(qty, 10);
        if( ! newQty || newQty < 0) {
            newQty = 0;
        }
        const modulo = newQty % normalizedPackUnit;
        newQty = newQty >= normalizedPackUnit ? newQty - modulo : normalizedPackUnit;
        if( (!min || newQty >= min) && (! max || newQty <= max) ) {
            setQty(newQty)
        }

        removeFreeItemAndCode(product, newQty);
    }

    const removeFreeItemAndCode = (row, qty = null) => {
        // check if base discount product is still in cart, if not remove free-item
        const promoCodeApplied = JSON.parse(sessionStorage.getItem('orders.applied_promo'));
        if (promoCodeApplied) {
            let clearFreeItems = false;
            let removeAppliedPromo = false;

            const discountType = promoCodeApplied.type;
            // check if free-item discount code
            if (discountType == 5) {
                // check if base discount-product is being removed
                if (row.ref === promoCodeApplied.productId) {
                    if (qty !== null) {
                        const promoMinQty = promoCodeApplied.minQuantity ?? 2;
                        const prodQty = qty / row.pack_unit;
                        if (prodQty < promoMinQty) {
                            clearFreeItems = true;
                            removeAppliedPromo = true;
                        } else {
                            applyPromoDiscount(promoCodeApplied);
                        }
                    } else {
                        clearFreeItems = true;
                        removeAppliedPromo = true;
                    }
                }
            }

            if (clearFreeItems) {
                // check and remove any free item if invalid promo
                const toRemoveFreeItems = items.filter((i) => i.freeItem === true);
                for (const k in toRemoveFreeItems) {
                    if (!Object.hasOwn(toRemoveFreeItems, k)) continue;
                    const itemToRemove = toRemoveFreeItems[k];

                    removeItem(itemToRemove.id);
                    removeAppliedPromo = true;
                }
            }

            if (removeAppliedPromo) {
                sessionStorage.removeItem('orders.applied_promo');
            }
        }
    }

    const applyPromoDiscount = (promoInfo) => {
        if (promoInfo === null) {
            sessionStorage.removeItem('orders.applied_promo');
        } else {
            sessionStorage.setItem('orders.applied_promo', JSON.stringify(promoInfo));
        }

        let clearFreeItems = false;
        let discountItemsCount = 0;
        const discountType = getAppliedDiscountType();
        if (discountType > 0) {
            for (const key in items) {
                if (Object.prototype.hasOwnProperty.call(items, key)) {
                    const product = items[key];

                    // ignore free item
                    if (product.freeItem) {
                        continue;
                    }

                    // TYPE_BUYX_GET1_FREE
                    if (discountType == 5) {
                        const freeProduct = getFreeProductBaseData(product, product.quantity, [], false);
                        const freeItemRef = 'F' + product.id;
                        // skip if not a valid discount product
                        if (freeProduct == null) {
                            // remove free item already added
                            const toRemoveFreeItem = items.find((i) => i.id === freeItemRef && i.freeItem == true);
                            if (toRemoveFreeItem !== undefined) {
                                removeItem(toRemoveFreeItem.id);
                            }
                            continue;
                        }
                        discountItemsCount++;

                        const prodPackUnit = getProductPackUnit(product.ref, product.pack_unit);
                        const prodPackQty = product.quantity / prodPackUnit;

                        const existingFreeItem = items.find((i) => i.id === freeItemRef && i.freeItem == true);

                        let promoMinQty = getFreeProductMinQty();
                        let freeQtyConst = Math.floor(prodPackQty / promoMinQty);
                        let freeItemQty = prodPackUnit * freeQtyConst;

                        if (existingFreeItem !== undefined) {
                            updateItem(getItem(freeItemRef).id, {
                                quantity: freeItemQty,
                                price: 0,
                                regular_price: freeProduct.regular_price,
                                discount: 0
                            })
                        } else {
                            addItem({
                                id: freeItemRef,
                                price: 0,
                                name: freeProduct.title,
                                image: freeProduct.images.length ? freeProduct.images : null,
                                pack_unit: prodPackUnit,
                                regular_price: freeProduct.regular_price,
                                discount: 0,
                                destock: freeProduct.destock,
                                freeItem: true,
                            }, freeItemQty);
                        }
                    } else {
                        clearFreeItems = true;
                        const discountPrice = getFinalDiscountPrice(product, product.quantity, [], false);
                        const discountPercentage = getFinalDiscountPercentage(product, product.quantity, [], false);

                        if (discountPercentage > 0) {
                            discountItemsCount++;
                        }
                        updateItem(
                            product.id, {
                                quantity: product.quantity,
                                price: discountPrice,
                                regular_price: product.regular_price,
                                discount: discountPercentage
                            }
                        );
                    }
                }
            }
        }

        if (clearFreeItems) {
            // check and remove any free item if not BXG1 promo
            const toRemoveFreeItems = items.filter((i) => i.freeItem === true);
            for (const k in toRemoveFreeItems) {
                if (!Object.hasOwn(toRemoveFreeItems, k)) continue;
                const itemToRemove = toRemoveFreeItems[k];

                removeItem(itemToRemove.id);
            }
        }
    
        if (promoInfo !== null && discountItemsCount === 0) {
            applyPromoDiscount(null);
        }
    }

    const handleAddToCart = () => {
        let cartPrice = price;
        let cartDiscount = discount;
        const promoCodeApplied = JSON.parse(sessionStorage.getItem('orders.applied_promo'));
        if (promoCodeApplied) {
            cartPrice = getFinalDiscountPrice(product, qty, [], false);
            cartDiscount = getFinalDiscountPercentage(product, qty, [], false);
        }

        inCart(product.ref)
            ? updateItem(getItem(product.ref).id, {
                quantity: qty,
                price: cartPrice,
                regular_price: product.regular_price,
                discount: cartDiscount
            })
            : addItem({
                id: product.ref,
                price: cartPrice,
                name: product.title,
                image: product.images?.length ? product.images : null,
                pack_unit: getProductPackUnit(product.ref, product.pack_unit),
                regular_price: product.regular_price,
                discount: cartDiscount,
                destock: product.destock
            }, qty);

        sessionStorage.setItem('backClicked', true);
        navigate(-1)
    }

    const handleRemoveFromCart = () => {

        if(inCart(product.ref)) {
            removeItem(getItem(product.ref).id);
            removeFreeItemAndCode(product);
        }

        navigate(-1)
    }

    
   

    const handleDiscountChange = new_discount => {

        if (new_discount === null) {
            setDiscount(getClientGeneralDiscount())
        } else {
            setDiscount(new_discount)
        }
        setMin(new_discount?.min ?? false)
        setMax(new_discount?.max ?? false)

       

        if(new_discount?.min && qty < new_discount.min)
        setQty(new_discount.min)
        else
        if(new_discount?.max && qty > new_discount.max+1)
        setQty(new_discount.max)

        
    }

    
    const recursiveFindCategory = (cats, ref) => {
        for(let i = 0; i < cats.length; i++){
            if(cats[i].ref == ref)
            return cats[i]

            const found = cats[i].children ? recursiveFindCategory(cats[i].children, ref) : false;

            if(found)
            return found;
        };

        return false;
    }

    const getProductsForCategory = (category) => {
        if (!category || !product) {
            return [];
        }

        const categoryRef = getProductCategoryRef(category);
        return allProducts.filter((candidateProduct) => (
            String(candidateProduct.ref) !== String(product.ref) &&
            isProductOnline(candidateProduct) &&
            candidateProduct.destock === product.destock &&
            productMatchesCategory(candidateProduct, categoryRef)
        ));
    }

    const findCategoryProduct = (direction) => {
        if (!product || !categories.length) {
            return null;
        }

        const currentCategory = product.categories ? recursiveFindCategory(categories, product.categories.ref) : null;
        if (!currentCategory) {
            return null;
        }

        const visibleCatalogCategories = categories.filter((category) => category.ref !== "destockage");
        const visibleCategories = flattenCategories(filterOnlineCategories(visibleCatalogCategories));
        const currentCategoryIndex = visibleCategories.findIndex(c => c.ref === currentCategory.ref);

        if (currentCategoryIndex === -1) {
            return null;
        }

        for (
            let categoryIndex = currentCategoryIndex + direction;
            categoryIndex >= 0 && categoryIndex < visibleCategories.length;
            categoryIndex += direction
        ) {
            const products = getProductsForCategory(visibleCategories[categoryIndex]);

            if (products.length) {
                return direction > 0 ? products[0] : products[products.length - 1];
            }
        }

        return null;
    }

    let currentIndex = categoryProducts.findIndex(p => p.ref === product.ref)
    

    let nextProduct = categoryProducts.length > currentIndex+1 ? categoryProducts[currentIndex+1] : null
    let previousProduct = currentIndex > 0 ? categoryProducts[currentIndex-1] : null
    let nextCategoryProduct = nextProduct ? null : findCategoryProduct(1)
    let previousCategoryProduct = previousProduct ? null : findCategoryProduct(-1)

    const navigateToProduct = (targetProduct, transitionClass) => {
        if (!targetProduct) {
            return;
        }

        setSlideState((state) => !state);
        setSlideFadeClass(transitionClass);
        navigate("/product/" + targetProduct.ref, { replace: true });
    }

    const shouldIgnoreProductSwipe = (target) => {
        return target?.closest?.('button, a, input, textarea, select, .cart-qty, .cart-update-qty, .gallery, .swiper-price');
    }

    const handleProductTouchStart = (event) => {
        const touch = event.touches?.[0];
        if (!touch || shouldIgnoreProductSwipe(event.target)) {
            productSwipeRef.current = null;
            return;
        }

        productSwipeRef.current = {
            x: touch.clientX,
            y: touch.clientY,
        };
    }

    const handleProductTouchEnd = (event) => {
        const start = productSwipeRef.current;
        const touch = event.changedTouches?.[0];
        productSwipeRef.current = null;

        if (!start || !touch || shouldIgnoreProductSwipe(event.target)) {
            return;
        }

        const deltaX = touch.clientX - start.x;
        const deltaY = touch.clientY - start.y;
        const minSwipeDistance = 60;

        if (Math.abs(deltaX) < minSwipeDistance || Math.abs(deltaX) < Math.abs(deltaY) * 1.4) {
            return;
        }

        if (deltaX < 0) {
            navigateToProduct(nextProduct ?? nextCategoryProduct, 'alt-fade');
        } else {
            navigateToProduct(previousProduct ?? previousCategoryProduct, 'fade');
        }
    }

    const gotoPreviousProduct = (e) => {
        e.preventDefault();
        navigateToProduct(previousProduct ?? previousCategoryProduct, 'fade');
    }

    const gotoNextProduct = (e) => {  
        
        e.preventDefault();
        navigateToProduct(nextProduct ?? nextCategoryProduct, 'alt-fade');
    }

    const pageItems = [
        
        <li className='page-item pe-3' key='page-backward'>
            <a href="#" onClick={ gotoPreviousProduct } className={'page-link' + (! previousProduct && ! previousCategoryProduct ? ' opacity-50':'') }><i className='fa fa-backward'></i></a>
        </li>
    ]     
    

    pageItems.push(
        <li className='page-item ps-3' key='page-forward'>
            <a href="#" onClick={ gotoNextProduct } className={'page-link' + (! nextProduct && ! nextCategoryProduct ? ' opacity-50':'') }><i className='fa fa-forward'></i></a>
        </li>
    )

    const recursiveFindProductQty = (ref, statuses) => {
        let count = 0;
        const statusList = Array.isArray(statuses) ? statuses : [statuses];
        let filteredOrders = orders.filter(o => statusList.includes(parseInt(o.status)));
        for (const key in filteredOrders) {
            if (Object.prototype.hasOwnProperty.call(filteredOrders, key)) {
                const order = filteredOrders[key];
                for (const k in order.items) {
                    if (Object.prototype.hasOwnProperty.call(order.items, k)) {
                        const orderItem = order.items[k];
                        const orderItemRef = orderItem.ref ?? orderItem.id;
                        if (String(orderItemRef) === String(ref)) {
                            count += parseFloat(orderItem.quantity) || 0;
                        }
                    }
                }
            }
        }
        return count;
    }
    
    const handleFutureStocks = (ref, defaultStockValue) => {
        if (ref) {
            let productQtyFromOrders = recursiveFindProductQty(ref, [1, 2, 3]);

            let futureStock = (parseFloat(defaultStockValue) || 0) - productQtyFromOrders;
            return futureStock > 0 ? futureStock : 0;
        }
        return parseFloat(defaultStockValue) || 0;
    }

    let showStockAvailability = true;
    const productInStockInfo = (product) => {
        let text = '';
        let actualStock = parseInt(product.stock);

        if (actualStock > 0) {
            text = strings.product_in_stock;
        }
        // if (actualStock <= 0 && forwardStock > 0) {
        if (actualStock <= 0) {
            text = strings.product_on_preorder;
        }

        return text;
    }

    let showProductBarCode = true;
    let showProductDescription = false;
    const DOMPurify = createDOMPurify()
    const formattedDeliveryDate = formatDeliveryDate(product?.delivery_date);

    return (
    currentClient === null && !demoClientAccess
    ? redirectToClientSelection(navigate, location.pathname)
    : <SwitchTransition mode={mode}>
        <CSSTransition
            key={slideState}
            nodeRef={slideRef}
            addEndListener={(done) => {
                if (slideRef.current) {
                    slideRef.current.addEventListener("transitionend", done, false);
                } else {
                    done();
                }
            }}
            classNames={slideFadeClass}
        >
        <div ref={slideRef} className='product-single h-100 d-flex flex-column justify-content-between' onTouchStart={ handleProductTouchStart } onTouchEnd={ handleProductTouchEnd }>
            <Ariane>
                <h1 className='titre text-center flex-grow-1 fs-4'>{ product?.ref+" - "+product?.title }</h1>
            </Ariane>
            <div className='container-xxl'>
                <div className='m-0 row border-1 border border-grey border-bottom-white'>
                    <div className='col-lg-7 media p-0 gallery-wrapper position-relative'>
                        { product?.images?.length > 0 ?
                        <Gallery images={ product.images } />
                        : ( product ? <img src={ notfound } className="img-contain img notfound" /> : '' ) }
                        { product?.destock && <div className='badge rounded-pill text-bg-warning position-absolute mt-3 top-0 me-3 end-0' style={ {zIndex:1000 } }>{ strings.destock }</div>}
                    </div>
                    <div className='col-lg-5 description p-0'>
                        
                        <Swiper 
                            ref={ swiperRef } 
                            effect="creative"
                            creativeEffect={{
                                prev: {
                                    shadow: true,
                                    translate: [0, 0, -100],
                                    opacity: 0
                                },
                                next: {
                                    translate: ["100%", 0, 0],
                                },
                            }}
                            modules={ [ EffectCreative ] } 
                            allowTouchMove={ false } 
                            className="h-100"
                            >
                            <SwiperSlide key="cart-item" className="d-flex flex-column justify-content-between">
                                <div className='px-5 pt-xl-4'>
                                    <TableRate prices={ product?.prices } active={ discount } handleChange={ handleDiscountChange }/>                                    
                                    <div className='container-fluid primary-color'>   
                                        
                                        <div className='row align-items-center justify-content-center'>
                                            <label className='col-xl-3 fs-4 mb-3 me-3'>{ strings.quantite }</label>
                                            <div className='col-xl-5 mb-3'>
                                                <QuantitySelector  locked="true" layout="horizontal" updateQuantity={ handleQtyChange } min={ min } max={ max } quantity={ qty } packUnit={ getProductPackUnit(product?.ref, product?.pack_unit) }/>
                                            </div>
                                        </div>
                                        <div className='row align-items-center justify-content-center border-bottom-grey mb-4 pb-4'>
                                            <label className='col-xl-3 fs-4 me-3'></label>
                                            <div className='col-xl-6'>
                                                <legend className='pt-2'>{ strings.formatString(strings.min_commande,{ min: getProductPackUnit(product?.ref, product?.pack_unit ?? 1)}) } <i className='fa fa-edit' onClick={ toggleProductUnitEdit }></i></legend>
                                                { showPackUnitEdit 
                                                    ? <QuantitySelector locked="true" layout="horizontal" updateQuantity={ handleProductPackUnitOverride } min={ normalizePositiveInteger(product?.sub_packaging, 1) } max={ max } quantity={ editedPackUnit } packUnit={ normalizePositiveInteger(product?.sub_packaging, 1) }/>
                                                    : ''
                                                }
                                            </div>
                                        </div>
                                        <div className='prix fs-4'>
                                            <div className='small'>{ strings.prix_fiche } : { price != product?.regular_price ? <><s className='me-1 small'>{ formatCurrency(product?.regular_price) }</s><span className=''>{ formatCurrency(price) }</span></> : formatCurrency(price) }</div>
                                            <strong>{ strings.total } : </strong>{ formatCurrency(total) }
                                        </div>
                                        {
                                            showStockAvailability && product ?
                                            <div className='prix fs-4'>
                                                <div className='small'>{ productInStockInfo(product) }</div>
                                            </div>
                                            : ''
                                        }
                                        {
                                            showProductBarCode ?
                                                <div>
                                                    <img src={getBarcodeUrl(product)} alt='' className="img img-contain small-cb" />
                                                    { getBarcodeLabel(product) }
                                                </div>
                                                : ''
                                        }
                                        <div className='row comment my-4 d-none'>
                                            <div className='col-12 p-0'>
                                                <textarea rows="4" className='form-control' placeholder={ strings.commentaires} />
                                            </div>
                                        </div>

                                        {
                                            showProductDescription && product && product?.description?.length ?
                                            <div className='row my-4'>
                                                <div className='col-12 p-0'>
                                                    <div 
                                                        style={{ height: '300px',overflow: 'auto',textAlign: 'left' }} 
                                                        dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(product?.description)}}
                                                        />
                                                </div>
                                            </div>
                                            : ''
                                        }
                                    </div>
                                </div>
                                <button onClick={ handleAddToCart } type='button' className='btn btn-primary btn-lg fs-2 w-100 text-white'>
                                    { strings.ajouter_panier } <i className='fa fa-cart-plus'></i>
                                </button>
                            </SwiperSlide>
                            <SwiperSlide key="product-info" className="infos">
                                <div className='px-5 pt-4'>
                                    <h3 className='text-center fs-2 mb-4'>{ strings.fiche_article }</h3>
                                    <table className='table table-secondary table-bordered'>
                                        <tbody>                                        
                                            <tr>
                                                <td>{ strings.utilisation }</td>
                                                <td>{ product?.utilisation }</td>
                                            </tr>
                                            <tr>
                                                <td>{ strings.matiere }</td>
                                                <td>{ product?.material }</td>
                                            </tr>
                                            <tr>
                                                <td>{ strings.longueur }</td>
                                                <td>{ product?.length }</td>
                                            </tr>
                                            <tr>
                                                <td>{ strings.largeur }</td>
                                                <td>{ product?.width }</td>
                                            </tr>
                                            <tr>
                                                <td>{ strings.hauteur }</td>
                                                <td>{ product?.height }</td>
                                            </tr>
                                            <tr>
                                                <td>{ strings.poids }</td>
                                                <td>{ product?.weight } kg</td>
                                            </tr>
                                            <tr>
                                                <td>{ strings.contenance }</td>
                                                <td>{ product?.capacity }</td>
                                            </tr>
                                            <tr>
                                                <td>{ strings.stock }</td>
                                                <td>{ product?.stock }</td>
                                            </tr>
                                            <tr>
                                                <td>{ strings.future_stock }</td>
                                                <td>{ product?.future_stock ?? handleFutureStocks(product?.ref, product?.stock) }</td>
                                            </tr>
                                            <tr>
                                                <td>{ strings.colisage }</td>
                                                <td>{ product?.packaging }</td>
                                            </tr>
                                            <tr>
                                                <td>{ strings.sous_colisage }</td>
                                                <td>{ product?.sub_packaging }</td>
                                            </tr>
                                            <tr>
                                                <td>{ strings.barcode }</td>
                                                <td>
                                                    <img src={getBarcodeUrl(product)} alt='' className="img img-contain" />
                                                    { getBarcodeLabel(product) }
                                                </td>
                                            </tr>
                                            { parseInt(product?.stock) <= 0 && formattedDeliveryDate ?
                                            <tr>
                                                <td>{ strings.datelivraison }</td>
                                                <td>{ formattedDeliveryDate }</td>
                                            </tr>
                                            : '' }
                                        </tbody>
                                    </table>
                                </div>
                            </SwiperSlide>  
                        </Swiper>
                        
                        
                       
                    </div>
                </div>
            </div>
            <BottomNavigation>
                <div className='col-4 text-start'>
                    { inCart(product?.ref) &&
                    <button className='btn-lg btn btn-danger' onClick={ handleRemoveFromCart }><i className='fa fa-times me-3'></i>{ strings.delete}</button> }
                </div>
                <div className="col-4 d-flex align-items-center justify-content-center">
                    <ul className='pagination justify-content-center m-0 pagination me-3'>
                        { pageItems }
                    </ul>
                </div>
                <div className='col-4 text-end'>
                    <SwiperBtn click={ (index) => swiperRef.current?.swiper.slideTo(index) } index="1" active={ swiperRef.current?.swiper.activeIndex == 1 }  activeElement={ <span>Fiche technique <i className='fa fa-times ms-3'></i></span> }>
                        <span>
                            { strings.fiche_technique } <i className='far fa-file-pdf ms-3'></i>
                        </span>
                    </SwiperBtn>
                    <button className='btn-lg btn border-white text-white d-none'>Ajouter au favoris <i className='fa fa-heart ms-3'></i></button>
                </div>
            </BottomNavigation>
        </div>
        </CSSTransition>
    </SwitchTransition>
    )
}

function FavoriteProductsList(){
    sessionStorage.setItem('browse_mode', 'list')
    sessionStorage.removeItem('catalog.current_selected_page');
    sessionStorage.removeItem('catalog.previous_selected_page');

    const { getAll } = useIndexedDB('catalog');
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] =  useState(12)
    const [currentClient, setCurrentClient] = useState(null);
    const [user, { updateFavoriteProducts }]  = useApi();

    let totalResult = 0
    let navigate = useNavigate();

    const showFavoritesPage = true;
    if (!showFavoritesPage) {
        navigate('/dashboard');
    }

    useEffect(() => {
        getAll().then(dataFromDB => {
            setProducts(dataFromDB.find(i => i.type == 'products')?.data ?? [])
            setOrders(dataFromDB.find(i => i.type === 'orders')?.data ?? [])
        });

        if (
            sessionStorage.getItem('backClicked') === 'true' && 
            sessionStorage.getItem('products.current_selected_page') &&
            sessionStorage.getItem('products.current_selected_page') !== page
        ) {
            let selectedPageNum = parseInt(sessionStorage.getItem('products.current_selected_page'));
            let selectedPagination = parseInt(sessionStorage.getItem('products.current_selected_pagination'));
            if (selectedPagination === pagination) {
                setPage(selectedPageNum);
            }
            setTimeout(() => {
                sessionStorage.setItem('backClicked', false);
            }, 300);
        }

        if (sessionStorage.getItem('orders.current_client')) {
            setCurrentClient(JSON.parse(sessionStorage.getItem('orders.current_client')));
        }
    }, []);

    let clientFavoriteProducts = getFavoriteProductRefs(currentClient?.favorite_products);
    const favoriteProducts = products.filter((p) => isProductOnline(p) && clientFavoriteProducts.includes(String(p.ref)));

    totalResult = favoriteProducts.length
    const totalPages = Math.ceil(totalResult / pagination)

    const gotoPreviousPage = () => {
        let previousPage = page-1

        if(previousPage >= 1)
        handleProductsPageChange( previousPage )
    }

    const gotoNextPage = () => {
        let nextPage = page+1;

        if(nextPage <=  totalPages)
        handleProductsPageChange( nextPage )
    }

    const pageItems = [
        <li className='page-item' key='page-backward'>
            <a href="#" onClick={ gotoPreviousPage } className={'page-link' + (page <= 1 ? ' opacity-50':'') }><i className='fa fa-backward'></i></a>
        </li>
    ];

    const getPaginationButtons = () => {
        if (totalPages <= 4) {
            const pages = [...new Array(totalPages)].map((_, index) =>
                (index + 1).toString()
            );
            return pages;
        }

        const pages = ['1'];

        if (page >= 4) {
            pages.push('...');
        }

        if (page + 1 > totalPages) {
            pages.push((page - 2).toString());
        }

        if (page - 1 > 1) {
            pages.push((page - 1).toString());
        }

        if (page !== 1) {
            pages.push(page.toString());
        }

        if (page + 1 <= totalPages) {
            pages.push((page + 1).toString());
        }

        if (page - 1 <= 0) {
            pages.push((page + 2).toString());
        }

        if (page + 2 < totalPages) {
            pages.push('...');
        }

        if (page + 1 < totalPages) {
            pages.push(totalPages.toString());
        }

        return pages;
    };

    const paginationButtons = getPaginationButtons();
    paginationButtons.forEach((val, idx) => {
        if (val === '...') {
            pageItems.push(
            <li key={ 'page_item_dd' + idx } className={ 'page-item' }>
                <a href="#" className='page-link'>...</a>
            </li>);
        } else {
            let i = parseInt(val);
            pageItems.push(
            <li key={ 'page_item_' + i } className={ page == i ? 'page-item active' : 'page-item' }>
                <a href="#" onClick={ () => { handleProductsPageChange(i);} } className='page-link'>{ i }</a>
            </li>);
        }
    });

    pageItems.push(
        <li className='page-item' key='page-forward'>
        <a href="#" onClick={ gotoNextPage } className={'page-link' + (page >= totalPages ? ' opacity-50':'') }><i className='fa fa-forward'></i></a></li>
    );

    const start = (page - 1)*pagination
    const end = start + pagination
    let itemClassNames = 'col-lg-3';
    switch(pagination){
        case 6:
            itemClassNames = 'col-lg-4'
            break
        case 12:
        default:
            itemClassNames = 'col-lg-3'
            break
    }
    const handleChangePagination = (e) => {
        setPagination(parseInt(e.target.value));
        handleProductsPageChange(1)
    }

    const handleProductsPageChange = (value) => {
        sessionStorage.setItem('products.current_selected_page', JSON.stringify(value));
        sessionStorage.setItem('products.current_selected_pagination', JSON.stringify(pagination));
        setPage(value);
    }

    const recursiveFindProductQty = (ref, statuses) => {
        let count = 0;
        const statusList = Array.isArray(statuses) ? statuses : [statuses];
        let filteredOrders = orders.filter(o => statusList.includes(parseInt(o.status)));
        for (const key in filteredOrders) {
            if (Object.prototype.hasOwnProperty.call(filteredOrders, key)) {
                const order = filteredOrders[key];
                for (const k in order.items) {
                    if (Object.prototype.hasOwnProperty.call(order.items, k)) {
                        const orderItem = order.items[k];
                        const orderItemRef = orderItem.ref ?? orderItem.id;
                        if (String(orderItemRef) === String(ref)) {
                            count += parseFloat(orderItem.quantity) || 0;
                        }
                    }
                }
            }
        }
        return count;
    }

    const handleFutureStocks = (ref, defaultStockValue) => {
        if (ref) {
            let productQtyFromOrders = recursiveFindProductQty(ref, [1, 2, 3]);

            let futureStock = (parseFloat(defaultStockValue) || 0) - productQtyFromOrders;
            return futureStock > 0 ? futureStock : 0;
        }
        return parseFloat(defaultStockValue) || 0;
    }

    const [ zoomImage, setZoomImage ] = useState(null);

    const removeProductFavorite = (product) => {
        const updatedClient = {
            ...currentClient,
            favorite_products: toggleFavoriteProductRef(currentClient?.favorite_products, product.ref, true)
        };

        sessionStorage.setItem('orders.current_client', JSON.stringify(updatedClient));
        setCurrentClient(updatedClient);
        updateFavoriteProducts(updatedClient);
    }

    return (
        currentClient === null
        ? navigate('/client')
        : <div className="products h-100 d-flex flex-column justify-content-between">
            <Ariane showSearch>
                <h1 className='titre text-center flex-grow-1'>{ strings.liste_favoris }</h1>
            </Ariane>
            <div className='products-list my-4 container-xxl'>
                <div className='products-list my-4 container-xxl p-0'>
                    <div className='row'>
                        {
                            favoriteProducts.slice(start,end).map( product => (
                                <ProductItem product={ product } className={ itemClassNames } key={ product.ref } withStock={ true } futureStockValue={ product.future_stock ?? handleFutureStocks(product.ref, product.stock) }  setZoomImage={setZoomImage} toggleProductFavorite={removeProductFavorite} selectedClient={currentClient} />
                            ))
                        }
                    </div>
                </div>
            </div>

            {
                zoomImage
                ? <div className="img-modal">
                    <div className="img-modal-content">
                        <span className="img-close" onClick={ () => setZoomImage(null) }>&times;</span>
                        <img src={ zoomImage } className="img img-contain" alt='' />
                    </div>
                </div>
                : <></>
            }

            <BottomNavigation>
                <Link to="/" className='fs-1 text-white col-2 text-start'>
                    <i className='fa fa-home'></i>
                </Link>
                <div className="col-8 d-flex align-items-center justify-content-center" style={ {zIndex:2}}>
                    <ul className='pagination justify-content-center m-0 pagination me-3'>
                        { pageItems }
                    </ul>
                    <div className="d-flex justify-content-center">
                        <div className="col-auto">
                            <label className="col-form-label">{ strings.afficher }</label>
                        </div>
                        <div className="col-auto">
                            <select name="product_num" value={ pagination } onChange={ handleChangePagination } className="ms-3 form-select">                        
                              
                                <option value="6">6 { strings.elements}</option>
                                <option value="12">12 { strings.elements}</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className='col-2'>
                    <MiniCart />
                </div>
            </BottomNavigation>
        </div> 
    )
}

export { ProductSingle, ProductList, ProductItem, FavoriteProductsList };

function SwiperBtn(props){

    const [active, setActive] = useState(props.active);
    const handleClick = () => {

        props.click(active ? 0 : props.index);
        setActive(!active);
    }
    
    return (
        <button className='btn-lg btn me-3 border-white text-white' onClick={ handleClick }>
            { active ? props.activeElement : props.children }
        </button>
    )
}

function Gallery(props){


    return (
        <Swiper slidesPerView="1" className="h-100 gallery" pagination={ { clickable: true } } modules={ [ Pagination ]}>
            { props.images.map( (image, index) => (
                <SwiperSlide key={ "gallery_" + index }>
                    <img src={ image } className="img img-contain" />
                </SwiperSlide>
            ))}
        </Swiper>
    )
}




function TableRate(props){
    
    
    const prices = props.prices?.filter(p => p.amount > 0)
    const setActive = (discount) => {

        if(active === discount)
        {            
            _setActive(null)
            props.handleChange(null)
        }        
        else
        {            
            _setActive(discount)
            props.handleChange(discount)
        } 
        
    }

    const [ active, _setActive ] = useState();
    
    useEffect( () => {

        _setActive(props.active)    
        if(props.active)
        swiperRef.current?.swiper.slideTo(1)
    }, [props.active])

    const sameDiscount = (a,b) => {
        return a.amount === b.amount && a.min === b.min && a.max === b.max
    }

    const swiperRef= useRef();


   
    return (

        <Swiper ref={ swiperRef } slidesPerView="1" className='swiper-price' nested={true} touchMoveStopPropagation={true}>
            <SwiperSlide>
            </SwiperSlide>
            <SwiperSlide>
                <table className='table table-secondary table-bordered'>
                    <thead>
                        <tr>
                            { prices?.map((p,index) => (
                                <th key={index} role="button" onClick={ e => { setActive(p) }}>{ p.min }</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            { prices?.map((p,index) => (
                                <td key={index} className={ active && sameDiscount(active,p)  ? 'bg-primary text-white' : '' } role="button" onClick={ e => { setActive(p) }}>{ p.amount }%</td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </SwiperSlide>
        </Swiper>  
    )
}

TableRate.defaultProps = {
active: null,
}